/**
 * ProjectProfileDialog — v1.8.0
 *
 * Per-project profile editor (admin only). Lives next to the existing
 * inline "Modifier" form in SettingsPage > ProjectRow; the inline form
 * still handles name / duration / description, while THIS dialog handles
 * the new project-specific configuration introduced by v1.8.0 :
 *
 *   - logo_url            : branding logo
 *   - ai_prompt           : custom system prompt for the AI auto-reply
 *   - welcome_message     : custom WhatsApp welcome template
 *   - quick_templates     : list of {label, body} entries surfaced in
 *                            the WhatsApp composer for THIS project
 *   - automation_enabled  : master kill-switch for the project's
 *                            automations (welcome sweep + AI reply)
 *
 * Each field is optional — leaving it empty means "inherit from global
 * settings" (handled server-side in _send_welcome_message / AI reply).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save,
  X,
  Settings as SettingsIcon,
  Plus,
  Trash2,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { projectsApi } from "@/lib/crmApi";
import { cn } from "@/lib/utils";

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open:boolean)=>void} props.onOpenChange
 * @param {object} props.projet — the project (must include id + nom; other
 *   v1.8.0 fields read with safe defaults)
 * @param {()=>void} [props.onSaved] — invoked after a successful PUT so the
 *   parent can refresh its list
 */
export default function ProjectProfileDialog({ open, onOpenChange, projet, onSaved }) {
  const [form, setForm] = useState(emptyForm(projet));
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("general");

  // Reset the form whenever the dialog re-opens or the project changes —
  // otherwise stale state from a previous project bleeds in.
  useEffect(() => {
    if (open) {
      setForm(emptyForm(projet));
      setActiveTab("general");
    }
  }, [open, projet?.id]);

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addTemplate = () => {
    setForm((f) => ({
      ...f,
      quick_templates: [...f.quick_templates, { label: "", body: "" }],
    }));
  };

  const updateTemplate = (idx, patch) => {
    setForm((f) => {
      const next = [...f.quick_templates];
      next[idx] = { ...next[idx], ...patch };
      return { ...f, quick_templates: next };
    });
  };

  const removeTemplate = (idx) => {
    setForm((f) => ({
      ...f,
      quick_templates: f.quick_templates.filter((_, i) => i !== idx),
    }));
  };

  const save = async () => {
    // Strip empty templates client-side so they don't poison the DB with
    // half-baked entries. Server enforces nothing stricter for now.
    const cleanTemplates = (form.quick_templates || []).filter(
      (t) => (t.label || "").trim() && (t.body || "").trim()
    );
    if (cleanTemplates.length !== (form.quick_templates || []).length) {
      // A template was dropped silently — toast a hint so the admin
      // doesn't think they "lost" it.
      toast.info(
        `${(form.quick_templates || []).length - cleanTemplates.length} template(s) vides ignorés.`
      );
    }
    setSaving(true);
    try {
      // We only send the v1.8.0 fields here. The inline "Modifier" form
      // already handles name/duree/description — we'd clobber if we sent
      // them too. Empty strings ARE sent so the admin can clear an
      // override and return to the global default.
      await projectsApi.update(projet.id, {
        logo_url: (form.logo_url || "").trim(),
        ai_prompt: form.ai_prompt || null,
        welcome_message: form.welcome_message || null,
        quick_templates: cleanTemplates,
        automation_enabled: form.automation_enabled,
        // v2.2.0 — stratégie d'attribution automatique des nouveaux leads
        attribution_strategy: form.attribution_strategy || "manual",
      });
      toast.success("Profil du projet enregistré");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403) {
        toast.error(detail || "Seul un admin peut modifier ce projet.");
      } else {
        toast.error(detail || e?.message || "Échec de l'enregistrement");
      }
    } finally {
      setSaving(false);
    }
  };

  const resetField = (k) => {
    if (k === "quick_templates") return updateField(k, []);
    updateField(k, k === "automation_enabled" ? true : "");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-[640px] max-h-[88vh] overflow-y-auto"
        data-testid="project-profile-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SettingsIcon className="h-5 w-5 text-accent-gold" />
            Profil projet — {projet?.nom}
          </DialogTitle>
          <DialogDescription>
            Configuration indépendante de ce projet : branding, message de bienvenue,
            instructions AI, templates rapides et automatisations. Réservé aux admins.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="general">Général</TabsTrigger>
            <TabsTrigger value="welcome">Welcome</TabsTrigger>
            <TabsTrigger value="ai">AI</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
          </TabsList>

          {/* ----- GENERAL: logo + automation kill switch ----- */}
          <TabsContent value="general" className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">URL du logo (optionnel)</Label>
              <Input
                value={form.logo_url}
                onChange={(e) => updateField("logo_url", e.target.value)}
                placeholder="https://…/logo.png"
                data-testid="profile-logo-url"
              />
              <p className="text-[11px] text-muted-foreground">
                Affiché dans l'inbox et le header du projet. Laisser vide pour utiliser le logo
                global de l'entreprise.
              </p>
              {form.logo_url && (
                <img
                  src={form.logo_url}
                  alt="Aperçu logo"
                  className="mt-2 h-12 rounded border border-border/60 bg-muted/20 object-contain p-1"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
            </div>

            <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label className="text-sm font-semibold">
                    Automatisations actives
                  </Label>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Quand désactivé, le welcome auto + AI auto-reply sont coupés pour CE projet
                    uniquement (les leads continuent d'être enregistrés).
                  </p>
                </div>
                <Switch
                  checked={form.automation_enabled}
                  onCheckedChange={(v) => updateField("automation_enabled", v)}
                  data-testid="profile-automation-toggle"
                />
              </div>
            </div>

            {/* v2.2.0 — Stratégie d'attribution automatique des leads */}
            <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
              <Label className="text-sm font-semibold">
                Attribution automatique des nouveaux leads
              </Label>
              <p className="mt-0.5 mb-2 text-[11px] text-muted-foreground">
                Quand un nouveau lead arrive sans commercial désigné, le CRM
                l'attribue automatiquement selon cette règle :
              </p>
              <select
                value={form.attribution_strategy}
                onChange={(e) => updateField("attribution_strategy", e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                data-testid="profile-attribution-select"
              >
                <option value="manual">
                  Manuel — aucune attribution auto (legacy)
                </option>
                <option value="round_robin">
                  Round-robin — cycle équitable entre commerciaux assignés
                </option>
                <option value="least_loaded">
                  Moins chargé — commercial avec le moins de leads ouverts
                </option>
              </select>
              <p className="mt-2 text-[10px] text-muted-foreground">
                Le pool de candidats = commerciaux dont les{" "}
                <b>projets assignés</b> incluent ce projet. À défaut, on
                tombe sur les admins actifs.
              </p>
            </div>
          </TabsContent>

          {/* ----- WELCOME message ----- */}
          <TabsContent value="welcome" className="mt-4 space-y-3">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">
                  Message de bienvenue WhatsApp (override)
                </Label>
                {form.welcome_message && (
                  <button
                    type="button"
                    onClick={() => resetField("welcome_message")}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground hover:text-foreground"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Hériter du global
                  </button>
                )}
              </div>
              <Textarea
                rows={5}
                value={form.welcome_message || ""}
                onChange={(e) => updateField("welcome_message", e.target.value)}
                placeholder={
                  "Bonjour {prenom}, merci de votre intérêt pour " + (projet?.nom || "ce programme") + " !"
                }
                data-testid="profile-welcome-message"
              />
              <p className="text-[11px] text-muted-foreground">
                Placeholders disponibles : <code className="rounded bg-muted px-1">{"{nom}"}</code>,{" "}
                <code className="rounded bg-muted px-1">{"{prenom}"}</code>. Vide = utilise le
                template global défini dans Paramètres → WhatsApp.
              </p>
            </div>
          </TabsContent>

          {/* ----- AI prompt ----- */}
          <TabsContent value="ai" className="mt-4 space-y-3">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Instructions AI pour les réponses auto (override)</Label>
                {form.ai_prompt && (
                  <button
                    type="button"
                    onClick={() => resetField("ai_prompt")}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground hover:text-foreground"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Hériter du global
                  </button>
                )}
              </div>
              <Textarea
                rows={9}
                value={form.ai_prompt || ""}
                onChange={(e) => updateField("ai_prompt", e.target.value)}
                placeholder={
                  "Ex : Tu es l'assistant commercial du projet " +
                  (projet?.nom || "X") +
                  ". Réponds en français, ton chaleureux, mets en avant les F3 à partir de 50M, propose une visite."
                }
                data-testid="profile-ai-prompt"
              />
              <p className="text-[11px] text-muted-foreground">
                Ces instructions s'ajoutent au prompt système de base. Spécifique à ce projet —
                ne pollue pas les réponses des autres projets.
              </p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
                <span>
                  Si "Automatisations actives" est coupé (onglet Général), l'AI ne répond pas
                  pour ce projet — quelle que soit la valeur du prompt.
                </span>
              </div>
            </div>
          </TabsContent>

          {/* ----- Quick templates ----- */}
          <TabsContent value="templates" className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-semibold">Templates rapides du composer</Label>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  Boutons affichés au-dessus du compositeur WhatsApp pour les leads de ce projet.
                  Vide = templates par défaut.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={addTemplate}
                className="h-7 gap-1.5"
                data-testid="profile-add-template"
              >
                <Plus className="h-3.5 w-3.5" />
                Ajouter
              </Button>
            </div>

            {(form.quick_templates || []).length === 0 ? (
              <div className="rounded-lg border border-dashed border-border/60 bg-muted/20 p-6 text-center">
                <p className="text-xs text-muted-foreground">
                  Aucun template personnalisé. Les templates par défaut seront utilisés.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {form.quick_templates.map((tpl, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border border-border/60 bg-card p-3"
                    data-testid={`profile-template-row-${idx}`}
                  >
                    <div className="flex gap-2">
                      <Input
                        value={tpl.label}
                        onChange={(e) =>
                          updateTemplate(idx, { label: e.target.value })
                        }
                        placeholder="Label (ex : 👋 Premier contact)"
                        className="h-8 flex-1 text-xs"
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeTemplate(idx)}
                        className="h-8 w-8 flex-none text-muted-foreground hover:text-red-500"
                        title="Supprimer ce template"
                        data-testid={`profile-remove-template-${idx}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <Textarea
                      rows={3}
                      value={tpl.body}
                      onChange={(e) =>
                        updateTemplate(idx, { body: e.target.value })
                      }
                      placeholder="Bonjour {prenom}, …"
                      className="mt-2 text-xs"
                    />
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">
                  Placeholders : <code className="rounded bg-muted px-1">{"{nom}"}</code> et{" "}
                  <code className="rounded bg-muted px-1">{"{prenom}"}</code>. Les templates
                  vides sont ignorés à l'enregistrement.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4 flex-col gap-2 sm:flex-row">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="gap-1.5"
          >
            <X className="h-3.5 w-3.5" />
            Annuler
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="gap-1.5"
            data-testid="profile-save-btn"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Produces a clean form state from a project doc — coerces missing fields
 *  to safe defaults so React inputs never receive `undefined`. */
function emptyForm(projet) {
  return {
    logo_url: projet?.logo_url || "",
    ai_prompt: projet?.ai_prompt || "",
    welcome_message: projet?.welcome_message || "",
    // Defensive copy so editing the array doesn't mutate the upstream object.
    quick_templates: Array.isArray(projet?.quick_templates)
      ? projet.quick_templates.map((t) => ({
          label: t?.label || "",
          body: t?.body || "",
        }))
      : [],
    automation_enabled:
      projet?.automation_enabled === undefined ? true : !!projet.automation_enabled,
    attribution_strategy: projet?.attribution_strategy || "manual",
  };
}
